import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getEbookPages } from "../../app/api";
import { useTranslation } from 'react-i18next';

const EbookReader = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [pages, setPages] = useState<string[]>([]);
  const [currentChunk, setCurrentChunk] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getEbookPages(id, currentChunk)
      .then((res) => {
        setPages(res.data.pages);
        setTotalPages(res.data.totalPages);
        setTotalChunks(res.data.totalChunks);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        if (err.response?.status === 403) {
          setError(t('ebookReader.noAccess'));
        }
        setLoading(false);
      });
    window.scrollTo(0, 0);
  }, [id, currentChunk]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="bg-danger-50 border border-danger-200 text-danger-700 px-8 py-6 rounded-2xl">
          <span className="font-semibold text-lg">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-neutral-100"
      onContextMenu={(e) => e.preventDefault()}
    >
      {/* Header bar */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-neutral-200">
        <div className="mx-auto max-w-3xl px-4 py-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-neutral-700">
            {t('ebookReader.page')} {(currentChunk - 1) * 10 + 1} - {Math.min(currentChunk * 10, totalPages)} {t('ebookReader.of')} {totalPages}
          </p>
          <div className="flex items-center gap-2">
            <button
              disabled={currentChunk <= 1}
              onClick={() => setCurrentChunk((c) => c - 1)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              ←
            </button>
            <span className="text-sm text-neutral-500">
              {currentChunk} / {totalChunks}
            </span>
            <button
              disabled={currentChunk >= totalChunks}
              onClick={() => setCurrentChunk((c) => c + 1)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-neutral-100 text-neutral-700 hover:bg-neutral-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              →
            </button>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-800 rounded-full animate-spin"></div>
        </div>
      )}

      {/* Page images */}
      <div className="mx-auto max-w-3xl px-4 py-8 space-y-2">
        {pages.map((url, i) => (
          <img
            key={`${currentChunk}-${i}`}
            src={url}
            draggable={false}
            className="w-full select-none pointer-events-none"
            style={{
              userSelect: 'none',
              WebkitUserSelect: 'none',
            }}
            alt={`Page ${(currentChunk - 1) * 10 + i + 1}`}
          />
        ))}
      </div>

      {/* Bottom pagination */}
      <div className="mx-auto max-w-3xl px-4 pb-12">
        <div className="flex justify-between items-center">
          <button
            disabled={currentChunk <= 1}
            onClick={() => setCurrentChunk((c) => c - 1)}
            className="px-6 py-3 text-sm font-semibold rounded-xl bg-white text-neutral-700 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            ← {t('ebookReader.prev')}
          </button>
          <span className="text-sm text-neutral-500">
            {currentChunk} / {totalChunks}
          </span>
          <button
            disabled={currentChunk >= totalChunks}
            onClick={() => setCurrentChunk((c) => c + 1)}
            className="px-6 py-3 text-sm font-semibold rounded-xl bg-white text-neutral-700 shadow-md hover:shadow-lg disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {t('ebookReader.next')} →
          </button>
        </div>
      </div>

      {/* Anti-copy CSS */}
      <style>{`
        img {
          -webkit-user-drag: none;
          -khtml-user-drag: none;
          -moz-user-drag: none;
          -o-user-drag: none;
        }
      `}</style>
    </div>
  );
};

export default EbookReader;
